import { get } from "lodash-es";
import { Dispatch, SetStateAction } from "react";
import {
	FieldErrors,
	FieldValues,
	Path,
	useController,
	UseControllerProps,
	UseFormRegister,
} from "react-hook-form";
import { HiXCircle } from "react-icons/hi";

import {
	cn,
	formatFileSizesWithPrefix,
	getFileTypeFromFileName,
} from "../../../lib";
import { ErrorMessage } from "./error-message";
import { FileIcon } from "./file-icon";
import { InputButton } from "./input-button";

interface FileUploadProps<TFormValues extends FieldValues>
	extends UseControllerProps<TFormValues> {
	name: Path<TFormValues>;
	register?: UseFormRegister<TFormValues>;
	errors?: FieldErrors<TFormValues>;
	setFileList: Dispatch<SetStateAction<File[]>>;
	fileList: File[];
	className?: string;
	buttonText?: string;
	maxFiles?: number;
	disabled?: boolean;
	accept?: string;
}

/** What's different from a-prime FormUploadSectionComponent:
 * 1. Error message is below Click to add file button
 * 2. Do not have title (text above FILENAME)
 * 3. Error message wrapper is <p> instead of react-fragment
 * 4. Optionally takes in className for main container
 * 5. Hide input button if fileList is >= maxFiles
 */
export function FileUpload<TFormValues extends Record<string, unknown>>({
	name,
	register,
	errors,
	control,
	defaultValue,
	setFileList,
	fileList,
	className,
	maxFiles,
	disabled,
	buttonText,
	accept,
}: FileUploadProps<TFormValues>) {
	const { field } = useController({ name, control, defaultValue });
	const hideInputButton =
		maxFiles != undefined ? fileList.length >= maxFiles : false;

	const errorMessages = get(errors, name);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleChange = (event: any) => {
		const updatedList = [...fileList, ...event.target.files];
		field.onChange(updatedList);
		setFileList(updatedList);
	};

	const fileRemove = (file: File) => {
		const updatedList = [...fileList];
		updatedList.splice(fileList.indexOf(file), 1);
		field.onChange(updatedList);
		setFileList(updatedList);
	};

	return (
		<div className={cn("mt-6", className)}>
			<div className="text-xs font-semibold text-[#68737D] mb-4">
				FILENAME
				<hr className="mt-4 h-0.5" />
				{fileList.map((file, index) => {
					const fileExtension = getFileTypeFromFileName(file.name);
					return (
						<div
							className={cn(
								"grid grid-cols-10 items-center gap-0 text-left font-light md:grid-cols-12",
								{ "hover:bg-slate-100": !disabled },
							)}
							key={file.name + index}
						>
							<div className="col-span-1 m-auto text-right">
								<FileIcon extension={fileExtension} />
							</div>
							<p className="col-span-5 pl-1 text-left text-sm sm:pl-0 md:col-span-8">
								{file.name}
							</p>
							<p className="col-span-3 pr-4 text-right text-sm md:col-span-2">
								{formatFileSizesWithPrefix(file.size)}
							</p>
							<HiXCircle
								color={disabled ? "#EDF2F7" : undefined}
								className={cn(
									"col-span-1 h-10 w-6 justify-end text-slate-400",
									{ "cursor-pointer": !disabled },
								)}
								onClick={disabled ? () => {} : () => fileRemove(file)}
							/>
						</div>
					);
				})}
			</div>
			{!hideInputButton && (
				<InputButton
					register={register}
					handleChange={handleChange}
					name={name}
					buttonText={buttonText}
					accept={accept}
					multiple={maxFiles === undefined || maxFiles > 1}
				/>
			)}
			{errorMessages && <ErrorMessage fieldError={errorMessages} />}
		</div>
	);
}
